import { useApolloClient, useFragment } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "solito/navigation";
import GatheringInitialClientEntryFragmentFragmentDoc from "src/api/graphql/__generated__/documents/GatheringInitialClientEntryFragmentFragmentDoc";
import GetGatheringForInitialClientEntryDocument from "src/api/graphql/__generated__/documents/GetGatheringForInitialClientEntryDocument";
import { GatheringInitialEntryFragmentFragment } from "src/api/graphql/__generated__/graphql";
import { biDirectionalGatheringRefCodeLookup } from "src/api/graphql/gatheringLookup";
import useParseGatheringIdentifier from "src/hooks/useParseGatheringIdentifier";

const useQueryGatheringEntry = (initialGathering?: GatheringInitialEntryFragmentFragment) => {
	const { identifier } = useParams<{ identifier: string }>();
	const input = useParseGatheringIdentifier(identifier);
	const [loading, setLoading] = useState(true);
	const apolloClient = useApolloClient();
	const [forceGatheringId, setForceGatheringId] = useState<string | undefined>(undefined);
	const { data: fragmentGathering } = useFragment({
		fragment: GatheringInitialClientEntryFragmentFragmentDoc,
		fragmentName: "GatheringInitialClientEntryFragment",
		from: {
			__typename: "Gathering",
			gatheringId:
				forceGatheringId ??
				input?.gatheringId ??
				(input?.refCode
					? biDirectionalGatheringRefCodeLookup[input?.refCode]
					: undefined) ??
				identifier,
		},
	});
	useEffect(() => {
		(async () => {
			try {
				const { data } = await apolloClient.query({
					query: GetGatheringForInitialClientEntryDocument,
					variables: {
						input,
					},
					fetchPolicy: "network-only",
				});
				setForceGatheringId(data.getGathering?.gatheringId);
			} finally {
				setLoading(false);
			}
		})();
	}, [apolloClient, input]);
	const gathering = useMemo<GatheringInitialEntryFragmentFragment | undefined>(() => {
		if (fragmentGathering?.gatheringId) {
			return fragmentGathering as GatheringInitialEntryFragmentFragment;
		}
		if (initialGathering) return initialGathering;
		return undefined;
	}, [fragmentGathering, initialGathering]);

	return { gathering, loading, identifier };
};
export default useQueryGatheringEntry;
